<script lang="ts">
    import { onMount, onDestroy, tick } from 'svelte';
    import { throttle, first, last, clamp } from 'lodash-es';
    import { getPdfContext } from '../context/PdfContext';
    import { parsePages } from '../utils/pageNumberUtils';
    import PdfPage from './PdfPage.svelte';
    import PdfTextLayer from './PdfTextLayer.svelte';

    export let pages: number[] | string | 'all' = 'all';
    export let renderTextLayer: boolean = true;
    export let pageClass: string = '';

    // 가상 스크롤링 활성화 여부
    export let enableVirtualScrolling: boolean = true;
    export let visibleBuffer: number = 10;
    export let scrollContainer: 'auto' | 'parent' = 'auto';

    const OBSERVER_ROOT_MARGIN = '100px';
    const OBSERVER_THRESHOLD = 0.5;
    const DEFAULT_PAGE_HEIGHT = 800;
    const SCROLL_THROTTLE_MS = 16; // ~60fps
    const HEIGHT_DIFF_THRESHOLD = 1;

    const { documentStore, navigationStore } = getPdfContext();

    let containerRef: HTMLDivElement;
    let detectedScrollContainer: HTMLElement | null = null;
    let intersectionObserver: IntersectionObserver | null = null;

    // Component State
    let mounted = false;
    let isScrollAdjusting = false;

    // Virtual Scrolling State
    let pageHeights = new Map<number, number>();
    let estimatedPageHeight = DEFAULT_PAGE_HEIGHT;
    let topSpacerHeight = 0;
    let bottomSpacerHeight = 0;

    // 전체 페이지 목록 계산
    $: allPages = pages === 'all' ? Array.from({ length: $documentStore.totalPages }, (_, i) => i + 1) : typeof pages === 'string' ? parsePages(pages) : pages;

    // 가상 스크롤링 가시 범위 계산
    $: visibleRange = enableVirtualScrolling ? calculateVisibleRange($navigationStore.currentPage, allPages.length, visibleBuffer) : null;

    // 렌더링할 페이지 필터링
    $: pagesToRender = enableVirtualScrolling && visibleRange ? allPages.filter(p => p >= visibleRange.start && p <= visibleRange.end) : allPages;

    // 페이지 로드 시 navigation store에 추가
    $: if (allPages.length > 0) {
        navigationStore.addPages(allPages);
    }

    // pagesToRender 변경 시 Observer 재설정 및 spacer 높이 업데이트
    $: if (mounted && enableVirtualScrolling && pagesToRender.length > 0) {
        tick().then(() => {
            updateSpacerHeights();
            observeRenderedPages();
        });
    }

    // 가상 스크롤링 활성화 여부 확인
    const isVirtualScrollingEnabled = (): boolean => {
        return enableVirtualScrolling && mounted && !!detectedScrollContainer;
    };

    // 가시 범위 계산
    const calculateVisibleRange = (currentPage: number, totalPages: number, buffer: number) => {
        const start = clamp(currentPage - buffer, 1, totalPages);
        const end = clamp(currentPage + buffer, 1, totalPages);
        return { start, end };
    };

    // 스크롤 컨테이너 감지
    const detectScrollContainer = (element: HTMLElement | null): HTMLElement | null => {
        if (!element || typeof window === 'undefined') return null;

        // 'parent' 모드: 직접 부모 사용
        if (scrollContainer === 'parent') {
            return element.parentElement || document.documentElement;
        }

        // 'auto' 모드: 스크롤 가능한 첫 번째 부모 찾기
        let current = element.parentElement;
        while (current && current !== document.body) {
            const { overflowY } = window.getComputedStyle(current);
            if (overflowY === 'auto' || overflowY === 'scroll') {
                return current;
            }
            current = current.parentElement;
        }

        return document.documentElement;
    };

    // 페이지 높이 가져오기 (실제 또는 예상)
    const getPageHeight = (pageNum: number): number => {
        return pageHeights.get(pageNum) ?? estimatedPageHeight;
    };

    // 페이지 높이 합계 계산
    const calculateHeightSum = (fromPage: number, toPage: number): number => {
        let sum = 0;
        for (let i = fromPage; i <= toPage; i++) {
            sum += getPageHeight(i);
        }
        return sum;
    };

    // Spacer 높이 업데이트
    const updateSpacerHeights = (): void => {
        if (!enableVirtualScrolling || pagesToRender.length === 0) {
            topSpacerHeight = 0;
            bottomSpacerHeight = 0;
            return;
        }

        const firstRendered = first(pagesToRender)!;
        const lastRendered = last(pagesToRender)!;

        topSpacerHeight = firstRendered > 1 ? calculateHeightSum(1, firstRendered - 1) : 0;
        bottomSpacerHeight = lastRendered < allPages.length ? calculateHeightSum(lastRendered + 1, allPages.length) : 0;
    };

    // 스크롤 위치로부터 중심 페이지 계산
    const calculateCenterPageFromScroll = (): number => {
        if (!detectedScrollContainer) return 1;

        const scrollTop = detectedScrollContainer.scrollTop;
        const containerHeight = detectedScrollContainer.clientHeight;
        const centerScrollPos = scrollTop + containerHeight / 2;

        let accumulatedHeight = 0;

        for (let page = 1; page <= allPages.length; page++) {
            const height = getPageHeight(page);

            if (accumulatedHeight + height > centerScrollPos) {
                return page;
            }

            accumulatedHeight += height;
        }

        return allPages.length;
    };

    // 스크롤 이벤트 핸들러
    const handleScroll = (): void => {
        if (!isVirtualScrollingEnabled() || isScrollAdjusting) return;

        const centerPage = calculateCenterPageFromScroll();

        if (centerPage !== $navigationStore.currentPage) {
            navigationStore.updateCurrentPage(centerPage);
        }
    };

    const throttledScroll = throttle(handleScroll, SCROLL_THROTTLE_MS, {
        leading: true, // 스크롤 시작 시 즉시 실행
        trailing: true, // 스크롤 종료 시 최종 값 반영
    });

    // 스크롤 위치 보정
    const adjustScrollPosition = (heightDiff: number): void => {
        if (!detectedScrollContainer) return;

        isScrollAdjusting = true;
        detectedScrollContainer.scrollTop += heightDiff;

        requestAnimationFrame(() => {
            isScrollAdjusting = false;
        });
    };

    // IntersectionObserver 콜백
    const handleIntersection = (entries: IntersectionObserverEntry[]): void => {
        entries.forEach(entry => {
            if (!entry.isIntersecting) return;

            const pageNum = parseInt(entry.target.getAttribute('data-page-number') || '0');
            if (pageNum > 0 && pageNum !== $navigationStore.currentPage) {
                navigationStore.updateCurrentPage(pageNum);
            }
        });
    };

    // IntersectionObserver 설정
    const setupIntersectionObserver = (): void => {
        if (!mounted || !containerRef) return;

        const root = detectedScrollContainer === document.documentElement ? null : detectedScrollContainer;

        intersectionObserver = new IntersectionObserver(handleIntersection, {
            root,
            rootMargin: OBSERVER_ROOT_MARGIN,
            threshold: OBSERVER_THRESHOLD,
        });

        observeRenderedPages();
    };

    // 렌더링된 페이지들 관찰
    const observeRenderedPages = (): void => {
        if (!intersectionObserver || !containerRef) return;

        const pageWrappers = containerRef.querySelectorAll('[data-page-number]');
        pageWrappers.forEach(wrapper => {
            intersectionObserver?.observe(wrapper);
        });
    };

    // IntersectionObserver 정리
    const cleanupIntersectionObserver = (): void => {
        if (intersectionObserver) {
            intersectionObserver.disconnect();
            intersectionObserver = null;
        }
    };

    // 페이지 렌더링 완료 핸들러
    const handlePageReady = (event: CustomEvent): void => {
        const { wrapper, pageNumber } = event.detail;
        const actualHeight = wrapper.offsetHeight;

        // 첫 페이지면 예상 높이 업데이트
        if (pageNumber === 1 && pageHeights.size === 0) {
            estimatedPageHeight = actualHeight;
        }

        const previousHeight = getPageHeight(pageNumber);
        pageHeights.set(pageNumber, actualHeight);

        const heightDiff = actualHeight - previousHeight;

        // 현재 보이는 페이지보다 위에 있는 페이지의 높이가 변경된 경우 스크롤 보정
        if (Math.abs(heightDiff) > HEIGHT_DIFF_THRESHOLD && pageNumber < $navigationStore.currentPage) {
            adjustScrollPosition(heightDiff);
        }

        updateSpacerHeights();
    };

    // 가상 스크롤링 초기화
    const initializeVirtualScrolling = async (): Promise<void> => {
        if (!enableVirtualScrolling || !mounted) return;

        await tick();

        detectedScrollContainer = detectScrollContainer(containerRef);

        if (!detectedScrollContainer) {
            console.warn('[PdfDocument] Could not detect scroll container');
            return;
        }

        setupIntersectionObserver();
        detectedScrollContainer.addEventListener('scroll', throttledScroll, { passive: true });
    };

    // 가상 스크롤링 정리
    const cleanupVirtualScrolling = (): void => {
        if (detectedScrollContainer) {
            detectedScrollContainer.removeEventListener('scroll', throttledScroll);
        }
        throttledScroll.cancel?.();
        cleanupIntersectionObserver();
        detectedScrollContainer = null;
    };

    onMount(async () => {
        mounted = true;
        await initializeVirtualScrolling();
    });

    onDestroy(() => {
        mounted = false;
        cleanupVirtualScrolling();
    });
</script>

<div bind:this={containerRef} class="flex w-full flex-col items-center">
    {#if enableVirtualScrolling && topSpacerHeight > 0}
        <div style="height: {topSpacerHeight}px;" aria-hidden="true" />
    {/if}

    {#each pagesToRender as pageNum (pageNum)}
        <PdfPage pageNumber={pageNum} class={pageClass} on:pageready={handlePageReady}>
            {#if renderTextLayer}
                <PdfTextLayer pageNumber={pageNum} />
            {/if}
            <slot {pageNum} />
        </PdfPage>
    {/each}

    {#if enableVirtualScrolling && bottomSpacerHeight > 0}
        <div style="height: {bottomSpacerHeight}px;" aria-hidden="true" />
    {/if}
</div>
